import { EntityType } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { ConstraintExtended, ConstraintType } from '@frontend/constraint/types';
import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { CommonMessage } from '@frontend/lang';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { DefaultContainer } from '../../modal/detail-modal/default-container/default-container';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';

export const CONSTRAINT_RULE_TITLE = (
    <FormattedMessage
        id='constraint-detail-modal.constraint-rule'
        defaultMessage='Constraint rule'
    />
);

export const ConstraintDetailsCard = ({ constraint }: { constraint: ConstraintExtended }) => (
    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={constraint}
            cols={2}
            order={['name', 'spot', 'module', 'slot', 'row', 'column']}
            keyOverwrite={
                new Map([
                    ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                    ['module', CommonMessage.OBJECTS.MODULE.DEFAULT],
                    ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                    ['name', CommonMessage.OBJECTS.CONSTRAINT.CONSTRAINT_NAME],
                    [
                        'row',
                        <FormattedMessage
                            key='constraints.rows-header'
                            id='constraints.rows-header'
                            defaultMessage='Row(s)'
                        />
                    ],
                    [
                        'column',
                        <FormattedMessage
                            key='constraints.columns-header'
                            id='constraints.columns-header'
                            defaultMessage='Column(s)'
                        />
                    ]
                ])
            }
            valueOverwrite={
                new Map<string, (obj: ConstraintExtended) => React.ReactNode>([
                    [
                        'spot',
                        (obj) =>
                            obj.spot ? (
                                <PlatformObjectBadge
                                    entityType={EntityType.SPOT}
                                    id={obj.spot.entity_id}
                                    display={obj.spot.name}
                                    foreign={obj.spot.foreign}
                                    accountId={accountIdFromResolvedEntity(obj.spot)}
                                />
                            ) : (
                                <span className='text-foreground-muted'>/</span>
                            )
                    ],
                    [
                        'module',
                        (obj) =>
                            obj.module ? (
                                <PlatformObjectBadge
                                    entityType={EntityType.MODULE}
                                    id={obj.module.entity_id}
                                    display={obj.module.name}
                                    parentIds={{ spot_id: obj.spot?.entity_id ?? obj.module.data?.spot_id }}
                                    foreign={obj.module.foreign}
                                    accountId={accountIdFromResolvedEntity(obj.module)}
                                />
                            ) : (
                                <span className='text-foreground-muted'>/</span>
                            )
                    ],
                    [
                        'slot',
                        (obj) =>
                            obj.slot ? (
                                <PlatformObjectBadge
                                    entityType={EntityType.SLOT}
                                    id={obj.slot.entity_id}
                                    display={obj.slot.name}
                                    parentIds={{
                                        spot_id: obj.spot?.entity_id ?? obj.slot.data?.spot_id,
                                        module_id: obj.module?.entity_id ?? obj.slot.data?.module_id
                                    }}
                                    foreign={obj.slot.foreign}
                                    accountId={accountIdFromResolvedEntity(obj.slot)}
                                />
                            ) : (
                                <span className='text-foreground-muted'>/</span>
                            )
                    ],
                    [
                        'row',
                        (obj) =>
                            obj.row !== null && obj.row_range !== null && obj.row_range !== undefined ? (
                                <span className='text-foreground-default'>
                                    {obj.row}-{obj.row + obj.row_range}
                                </span>
                            ) : obj.row !== null ? (
                                <span className='text-foreground-default'>{obj.row}</span>
                            ) : null
                    ],
                    [
                        'column',
                        (obj) =>
                            obj.column !== null && obj.column_range !== null && obj.column_range !== undefined ? (
                                <span className='text-foreground-default'>
                                    {obj.column}-{obj.column + obj.column_range}
                                </span>
                            ) : obj.column !== null ? (
                                <span className='text-foreground-default'>{obj.column}</span>
                            ) : null
                    ]
                ])
            }
        />
    </div>
);

export const ConstraintRuleSection = ({ constraint }: { constraint: ConstraintExtended }) => (
    <DefaultContainer
        title={CONSTRAINT_RULE_TITLE}
        content={
            <div className='w-full'>
                <PlatformDetailGrid
                    object={constraint}
                    order={['mode', 'type', 'data']}
                    cols={2}
                    valueOverwrite={
                        new Map<string, (obj: ConstraintExtended) => React.ReactNode>([
                            ['data', (obj) => formatConstraintData(obj.data, obj.type)],
                            [
                                'mode',
                                (obj) => (
                                    <DefaultBadge
                                        label={(obj.mode.charAt(0).toUpperCase() + obj.mode.slice(1)).replace(/_/g, ' ')}
                                        styles={{ container: { color: 'bg-brand-muted' } }}
                                    />
                                )
                            ],
                            ['type', (obj) => (obj.type.charAt(0).toUpperCase() + obj.type.slice(1)).replace(/_/g, ' ')]
                        ])
                    }
                />
            </div>
        }
    />
);

export const formatConstraintData = (data: any, type: ConstraintType): React.ReactNode => {
    if (!data) return '';

    if (data.product_id) {
        return (
            <div>
                <PlatformObjectBadge
                    entityType={EntityType.PRODUCT}
                    id={data.product_id}
                />
            </div>
        );
    }
    if (data.equipment_id) {
        return (
            <div>
                <PlatformObjectBadge
                    entityType={EntityType.EQUIPMENT}
                    id={data.equipment_id}
                />
            </div>
        );
    }
    if (data.account_id) {
        return (
            <div className='flex gap-1'>
                <PlatformObjectBadge
                    entityType={EntityType.ACCOUNT}
                    id={data.account_id}
                    key={data.account_id}
                />
            </div>
        );
    }
    if ('width' in data || 'height' in data || 'depth' in data) {
        const { width, height, depth } = data;
        const parts = [];
        if (width !== undefined && width !== null) parts.push(`W: ${width}`);
        if (height !== undefined && height !== null) parts.push(`H: ${height}`);
        if (depth !== undefined && depth !== null) parts.push(`D: ${depth}`);
        return (
            <div className='flex gap-1'>
                <span>{parts.length > 0 ? parts.join(' x ') : ''}</span>
            </div>
        );
    }
    if (data.type) {
        return (
            <div className='flex gap-1'>
                <span>{(data.type.charAt(0).toUpperCase() + data.type.slice(1)).replace(/_/g, ' ')}</span>
            </div>
        );
    }
    if (type === ConstraintType.APPLICATION && data.application) {
        return <div>{getApplicationDisplayName(data.application as Application)}</div>;
    }
    if (data.state) {
        return `${(data.state.charAt(0).toUpperCase() + data.state.slice(1)).replace(/_/g, ' ')}`;
    }
    if (data.quantity !== undefined) {
        return `${data.quantity}`;
    }

    return JSON.stringify(data);
};
